import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
/** Styles Import */
import "./Style.css";
/** Services Import */
import Global from "../../Services/Common/Global";
import Commons from "../../Services/Common/Common";
/** Redux Import */
import { useSelector } from "react-redux";
/** Components Import */
import { Card, Image } from "react-bootstrap";
import ReactPlayer from "react-player";
import AutoTranslate from "../../Components/AutoTranslate";
/** Icons Import */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleRight,
  faEllipsisVertical,
  faPause,
  faPlay,
  faVolumeHigh,
  faVolumeXmark,
  faExpand,
  faTriangleExclamation,
  faForward,
  faBackward,
  faClosedCaptioning
} from "@fortawesome/free-solid-svg-icons";

const IS_IOS = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());

function PlayVideo() {
  const navigate = useNavigate();
  const location = useLocation();
  const language = useSelector((state) => state.localStorage.language);
  const [relatedContents, setRelatedContents] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [lastVolume, setLastVolume] = useState(0.8);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [showCenterIcon, setShowCenterIcon] = useState(false);
  const [centerIcon, setCenterIcon] = useState(faPlay);
  const [seekAnimation, setSeekAnimation] = useState({ show: false, type: '' });
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(true);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [hoverTooltip, setHoverTooltip] = useState({ show: false, position: 0, time: '0:00' });
  const [isControlsVisible, setIsControlsVisible] = useState(false);
  const clickTimer = useRef(null);
  const [lastClickTime, setLastClickTime] = useState(0);
  

  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const progressBarRef = useRef(null);
  const controlsHideTimer = useRef(null);

  let contentData = location.state?.data;
  if (contentData && typeof contentData.content === "object" && contentData.content !== null) {
    contentData = contentData.content;
  }
  const contentType = contentData?.contentType || location.state?.data?.contentType;

  const memoizedContentData = useMemo(() => {
    return contentData;
  }, [contentData?.id, contentData?.content]);

  useEffect(() => {
    if (!location.state?.data) {
      return;
    }
    setPlayedSeconds(0);
    setVideoDuration(0);
    setPlaying(false);
    setIsVideoLoading(false);
    setIsVideoReady(false);
    const timer = setTimeout(() => {
      Init();
    }, 100);
    return () => {
      clearTimeout(timer);
      setPlaying(false);
    };
  }, [location.state]);

  useEffect(() => {
    return () => {
      setPlaying(false);
      if (playerRef.current) {
        try {
          const playerElement = playerRef.current.getInternalPlayer();
          if (playerElement) {
            playerElement.pause();
            playerElement.src = "";
            playerElement.load();
          }
        } catch (e) {
          console.log("[Watch] ReactPlayer 정리 중 오류:", e);
        }
      }
      Commons.cleanupMediaElements();
      Commons.disableMediaSession();
    };
  }, []);

  useEffect(() => {
    if (showCenterIcon) {
      const timer = setTimeout(() => {
        setShowCenterIcon(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showCenterIcon]);

  useEffect(() => {
    if (seekAnimation.show) {
      const timer = setTimeout(() => {
        setSeekAnimation({ show: false, type: '' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [seekAnimation]);

  useEffect(() => {
    if (isVideoReady && playerRef.current?.getInternalPlayer()) {
      const videoElement = playerRef.current.getInternalPlayer();
      if (videoElement.textTracks?.length > 0) {
        videoElement.textTracks[0].mode = subtitlesEnabled ? 'showing' : 'hidden';
      }
    }
  }, [subtitlesEnabled, isVideoReady]);

  useEffect(() => {
    const handleScrubMove = (e) => {
      if (!isScrubbing || !progressBarRef.current || videoDuration === 0) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const rect = progressBarRef.current.getBoundingClientRect();
      const scrubRatio = Math.min(Math.max(0, clientX - rect.left), rect.width) / rect.width;
      const scrubTime = scrubRatio * videoDuration;
      setPlayedSeconds(scrubTime);
      setHoverTooltip({
        show: true,
        position: scrubRatio * 100,
        time: formatTime(scrubTime),
      });
    };
    const handleScrubMouseUp = (e) => {
      if (isScrubbing) {
        const rect = progressBarRef.current.getBoundingClientRect();
        const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
        const scrubRatio = Math.min(Math.max(0, clientX - rect.left), rect.width) / rect.width;
        const newTime = scrubRatio * videoDuration;
        playerRef.current.seekTo(newTime);
        setIsScrubbing(false);
        setHoverTooltip({ show: false, position: 0, time: '0:00' });
        setPlaying(true);
      }
    };
    if (isScrubbing) {
      window.addEventListener('mousemove', handleScrubMove);
      window.addEventListener('touchmove', handleScrubMove);
      window.addEventListener('mouseup', handleScrubMouseUp);
      window.addEventListener('touchend', handleScrubMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleScrubMove);
      window.removeEventListener('touchmove', handleScrubMove);
      window.removeEventListener('mouseup', handleScrubMouseUp);
      window.removeEventListener('touchend', handleScrubMouseUp);
    };
  }, [isScrubbing, videoDuration]);

  const Init = async () => {
    try {
      await Search();
    } finally { }
  };

  const Search = async () => {
    try {
      let params = contentData.tags && contentData.tags[0] ? { q: contentData.tags[0] } : {};
      const result = await Commons.Axios("get", Global.base.useUrl + Global.urls.Content.Search, params);
      let data = result.data;
      if (!data || data.type === "Error") {
        throw new Error(data.message || data);
      } else {
        setRelatedContents(data.items);
      }
    } catch (e) {
      console.log("Search Error: ", e);
    }
  };

  const Sumit = async (_param) => {
    try {
      await Commons.Axios("post", `${Global.base.useUrl + Global.urls.Content.View}/${_param}`, {}, {});
    } catch (e) {
      console.log("Sumit Error: ", e);
    }
  };

  const isPDF = contentType && contentType.toLowerCase() === "pdf";
  const isVideo = contentType && contentType.toLowerCase() === "video";

  const togglePlayPause = () => {
    setPlaying(prev => !prev);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0) {
      setLastVolume(newVolume);
    }
  };

  const handleMuteToggle = () => {
    if (volume > 0) {
      setLastVolume(volume);
      setVolume(0);
    } else {
      setVolume(lastVolume > 0 ? lastVolume : 0.8);
    }
  };

  const handleProgress = (state) => {
    if (!isScrubbing) {
      setPlayedSeconds(state.playedSeconds);
    }
  };

  const handleDuration = (duration) => {
    setVideoDuration(duration);
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    if (isScrubbing) return;
    const progressBar = progressBarRef.current;
    if (progressBar && playerRef.current) {
      const rect = progressBar.getBoundingClientRect();
      const clickPositionX = e.clientX - rect.left;
      const seekRatio = clickPositionX / rect.width;
      const seekToSeconds = videoDuration * seekRatio;
      playerRef.current.seekTo(seekToSeconds);
      setPlayedSeconds(seekToSeconds);
    }
  };

  const handleFullscreen = () => {
    const playerElement = playerRef.current?.getInternalPlayer();
    const containerElement = videoContainerRef.current;
    const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement;
    if (!isFullscreen) {
      if (playerElement && typeof playerElement.webkitEnterFullscreen === 'function') {
        playerElement.webkitEnterFullscreen();
      } else if (containerElement?.requestFullscreen) {
        containerElement.requestFullscreen();
      } else if (containerElement?.webkitRequestFullscreen) {
        containerElement.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  const handleVideoAreaClick = (e) => {
    const now = new Date().getTime();
    if (now - lastClickTime < 300) {
      clearTimeout(clickTimer.current);
      const rect = videoContainerRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      let newTime = playedSeconds;
      let seekType = '';
      if (clickX > rect.width / 2) {
        newTime = Math.min(playedSeconds + 10, videoDuration);
        seekType = 'forward';
      } else {
        newTime = Math.max(playedSeconds - 10, 0);
        seekType = 'backward';
      }
      playerRef.current.seekTo(newTime);
      setPlayedSeconds(newTime);
      setSeekAnimation({ show: true, type: seekType });
      setLastClickTime(0);
    } else {
      clearTimeout(clickTimer.current);
      clickTimer.current = setTimeout(() => {
        setCenterIcon(playing ? faPlay : faPause);
        setShowCenterIcon(true);
        togglePlayPause();
      }, 300);
    }
    setLastClickTime(now);
  };

  const handleToggleSubtitles = () => {
    setSubtitlesEnabled(prev => !prev);
  };

  const getSubtitleTracks = () => {
    if (!contentData) {
      return [];
    }
    const { id, title } = contentData;
    if (title === '감전사고') {
      switch (language.lang) {
        case 'vi':
          return [{
            kind: 'subtitles',
            src: '/subtitles/gamjeunTest-20-vi.vtt', // 👈 베트남어 자막 파일
            srcLang: 'vi',
            label: 'Tiếng Việt',
            default: false
          }];
        case 'en':
          return [{
            kind: 'subtitles',
            src: '/subtitles/gamjeunTest-20-en.vtt', // 👈 영어 자막 파일
            srcLang: 'en',
            label: 'English',
            default: false
          }];
        default: // 'ko'를 포함한 모든 나머지 경우
          return [{
            kind: 'subtitles',
            src: '/subtitles/gamjeunTest-20-ko.vtt', // 👈 한국어 자막 파일
            srcLang: 'ko',
            label: '한국어',
            default: true
          }];
      }
    }
    if (['추락', '추락사고'].includes(title)) {
      if (language.lang === 'vi') {
        return [{ kind: 'subtitles', src: '/subtitles/vttSubtitles_vi.vtt', srcLang: 'vi', label: 'Tiếng Việt', default: true }];
      } else {
        return [{ kind: 'subtitles', src: '/subtitles/vttSubtitles.vtt', srcLang: 'ko', label: '한국어', default: true }];
      }
    }
    if (id === 11 && title === '개인보호구') {
      return [{ kind: 'subtitles', src: '/subtitles/20250704142256_dfb4c02a9f964462a0ea8c386ea6b301.vtt', srcLang: 'ko', label: '한국어', default: true }];
    }
    return [];
  };

  function formatTime(seconds) {
    if (isNaN(seconds) || seconds < 0) return '0:00';
    const date = new Date(seconds * 1000);
    const mm = date.getUTCMinutes();
    const ss = date.getUTCSeconds().toString().padStart(2, '0');
    return `${mm}:${ss}`;
  }

  const hasSubtitles = getSubtitleTracks().length > 0;

  const handleScrubMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!videoDuration) return;
    setIsScrubbing(true);
    setPlaying(false);
  };

  const handleMouseMove = (e) => {
    if (isScrubbing || !progressBarRef.current || videoDuration === 0) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const hoverRatio = (e.clientX - rect.left) / rect.width;
    const hoverTime = hoverRatio * videoDuration;
    setHoverTooltip({
      show: true,
      position: hoverRatio * 100,
      time: formatTime(hoverTime),
    });
  };

  const handleMouseLeave = () => {
    setHoverTooltip({ show: false, position: 0, time: '0:00' });
  };

  return (
    <div >
      <div className={`${Commons.IsMobile() ? "flex-column" : "flex-start"} gap-3`}>
        <div className='watch-video-container'>
          {IS_IOS ? (
            <div
              className={`ios-player-layout ${isControlsVisible ? 'controls-active' : ''}`}
              onMouseEnter={() => setIsControlsVisible(true)}
              onMouseLeave={() => setIsControlsVisible(false)}
              style={{ position: 'relative', height: isPDF ? "70vh" : "auto", backgroundColor: 'black' }}
            >
              {isPDF ? (
                <iframe src={`${contentData.content || location.state.data.content}#toolbar=1&scrollbar=1`} width='100%' style={{ border: "none", backgroundColor: "white", minHeight: "70vh" }} title='PDF Viewer' />
              ) : isVideo && contentData?.content && contentData?.content.trim() !== '' ? (
                <ReactPlayer
                  width='100%'
                  height='100%'
                  style={{ height: "100%", overflow: "hidden", backgroundColor: "black" }}
                  url={memoizedContentData?.content || contentData?.content || ''}
                  playing={true}
                  controls={true}
                  playsInline={true}
                  config={{
                    file: {
                      attributes: {
                        playsInline: true,
                        "webkit-playsInline": "true",
                        disablePictureInPicture: true,
                      },
                      tracks: getSubtitleTracks()
                    }
                  }}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'rgba(255, 255, 255, 0.7)', backgroundColor: 'black' }}>
                  <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '40px', marginBottom: '1rem' }} />
                  {!contentData ? (<AutoTranslate>콘텐츠를 불러오는 중...</AutoTranslate>) : (<AutoTranslate>지원하지 않는 콘텐츠 형식입니다.</AutoTranslate>)}
                </div>
              )}
            </div>
          ) : (
            <div
              ref={videoContainerRef}
              className={`watch-video-container-video ${isControlsVisible ? 'controls-active' : ''}`}
              style={{ position: 'relative', height: isPDF ? "70vh" : "auto", backgroundColor: 'black' }}
              onClick={!isPDF ? handleVideoAreaClick : undefined}
              onMouseEnter={() => setIsControlsVisible(true)}
              onMouseLeave={() => {
                // 드래그 중이 아닐 때만 숨김
                if (!isScrubbing) {
                  setIsControlsVisible(false);
                }
              }}
              onMouseMove={() => {
                // 컨트롤러를 보여주고, 기존 타이머는 초기화
                setIsControlsVisible(true);
                clearTimeout(controlsHideTimer.current);
          
                // 3초 후에 컨트롤러를 숨기는 새로운 타이머 설정
                controlsHideTimer.current = setTimeout(() => {
                  setIsControlsVisible(false);
                }, 3000);
              }}
            >
              {isPDF ? (
                <iframe src={`${contentData.content || location.state.data.content}#toolbar=1&scrollbar=1`} width='100%' style={{ border: "none", backgroundColor: "white", minHeight: "70vh" }} title='PDF Viewer' />
              ) : isVideo && contentData?.content && contentData?.content.trim() !== '' ? (
                <>
                  {isVideoLoading && (<div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', fontSize: '1.2rem', zIndex: 10 }}><AutoTranslate>동영상 로딩 중...</AutoTranslate></div>)}
                  <ReactPlayer
                    ref={playerRef}
                    width='100%'
                    height='100%'
                    style={{ height: "100%", overflow: "hidden", backgroundColor: "black" }}
                    url={memoizedContentData?.content || contentData?.content || ''}
                    key={`${memoizedContentData?.id || contentData?.id}-${memoizedContentData?.content || contentData?.content}`}
                    playing={playing}
                    volume={volume}
                    controls={false}
                    onProgress={handleProgress}
                    onDuration={handleDuration}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onReady={() => { setIsVideoLoading(false); setIsVideoReady(true); setPlaying(true); if ('mediaSession' in navigator) { navigator.mediaSession.metadata = null; navigator.mediaSession.playbackState = 'none'; } }}
                    onLoadStart={() => { setIsVideoLoading(true); }}
                    onError={(e) => { setIsVideoLoading(false); setIsVideoReady(false); setPlaying(false); }}
                    onEnded={() => { setPlaying(false); }}
                    config={{
                      file: {
                        attributes: {
                          playsInline: true,
                          "webkit-playsInline": "true",
                          disablePictureInPicture: true,
                          preload: "metadata",
                        },
                        tracks: getSubtitleTracks()
                      }
                    }}
                  />

                  {showCenterIcon && (<div className="center-icon-animation-overlay"><FontAwesomeIcon icon={centerIcon} /></div>)}
                  {seekAnimation.show && seekAnimation.type === 'backward' && (<div className="seek-animation-overlay backward"><FontAwesomeIcon icon={faBackward} /><span>10</span></div>)}
                  {seekAnimation.show && seekAnimation.type === 'forward' && (<div className="seek-animation-overlay forward"><span>10</span><FontAwesomeIcon icon={faForward} /></div>)}

                  <div className={`custom-controls-container ${isControlsVisible ? 'visible' : ''}`}>
                    <div ref={progressBarRef} className="progress-bar-wrapper" onMouseDown={handleScrubMouseDown} onTouchStart={handleScrubMouseDown} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onClick={handleSeek}>
                      <div className="progress-bar-container">
                        {hoverTooltip.show && (<div className="progress-tooltip" style={{ left: `${hoverTooltip.position}%` }}>{hoverTooltip.time}</div>)}
                        <div className="progress-bar-filled" style={{ width: videoDuration > 0 ? `${(playedSeconds / videoDuration) * 100}%` : '0%' }} />
                      </div>
                    </div>
                    <div className="controls-wrapper">
                      <div className="controls-group">
                        <button onClick={(e) => { e.stopPropagation(); togglePlayPause(); }} className="control-btn"><FontAwesomeIcon icon={playing ? faPause : faPlay} /></button>
                        <div className="volume-container" onClick={(e) => e.stopPropagation()}>
                          <FontAwesomeIcon icon={volume > 0 ? faVolumeHigh : faVolumeXmark} className="volume-icon" onClick={handleMuteToggle} style={{ cursor: 'pointer' }} />
                          <input type="range" min={0} max={1} step={0.05} value={volume} onChange={handleVolumeChange} className="volume-slider" />
                        </div>
                        <div className="time-display">{formatTime(playedSeconds)} / {formatTime(videoDuration)}</div>
                      </div>
                      <div className="controls-group">
                        {hasSubtitles && (<button onClick={(e) => { e.stopPropagation(); handleToggleSubtitles(); }} className="control-btn" style={{ color: subtitlesEnabled ? '#03bbfe' : 'white' }} title="자막"><FontAwesomeIcon icon={faClosedCaptioning} /></button>)}
                        <button onClick={(e) => { e.stopPropagation(); handleFullscreen(); }} className="control-btn"><FontAwesomeIcon icon={faExpand} /></button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'rgba(255, 255, 255, 0.7)', backgroundColor: 'black' }}>
                  <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '40px', marginBottom: '1rem' }} />
                  {!contentData ? (<AutoTranslate>콘텐츠를 불러오는 중...</AutoTranslate>) : (<AutoTranslate>지원하지 않는 콘텐츠 형식입니다.</AutoTranslate>)}
                </div>
              )}
            </div>
          )}

          <div className='my-2'>
            <Card>
              <Card.Header className={`${Commons.IsMobile() ? "flex-column" : "flex-between"}`}>
                <Card.Title style={{ fontSize: "1.5rem", fontWeight: 500, marginTop: "0.5rem", marginBottom: "-0.5rem" }}><AutoTranslate>{contentData?.title}</AutoTranslate></Card.Title>
                {Array.isArray(contentData?.tags) && contentData.tags.length > 0 && (
                  <div style={{ margin: "0.5rem 0 -0.5rem 0", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                    {contentData.tags.map((tag, idx) => (<span key={idx} style={{ color: "#43a0fe", cursor: "pointer", fontWeight: 500 }} onClick={() => navigate("/search-result", { state: { data: { items: [] }, searchText: tag } })}>#{tag}</span>))}
                  </div>
                )}
              </Card.Header>
              <Card.Body>
                <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem" }}>
                  <Card.Text><AutoTranslate>{`조회 수 ${contentData?.viewCount ? contentData.viewCount : 0}회`}</AutoTranslate></Card.Text>
                  <span style={{ color: "#bbb", fontSize: "0.8rem" }}>|</span>
                  <Card.Text><AutoTranslate>{`${contentData?.createdAt ? contentData.createdAt.toString().slice(0, 10) : ""}`}</AutoTranslate></Card.Text>
                </div>
                <Card.Text><AutoTranslate>{contentData?.summary}</AutoTranslate></Card.Text>
              </Card.Body>
            </Card>
          </div>
        </div>
        <div className='watch-list-container'>
          {relatedContents.map((item, index) => (
            <div key={index} className='flex-start mb-2 gap-2 cursor-pointer watch-list-item'
              onClick={() => {
                window.scrollTo(0, 0);
                Sumit(item.id);
                navigate("/watch", { state: { data: { content: item }, searchText: location.state.searchText, replace: true } });
              }}>
              <div><Image className='watch-list-item-image' src={item.thumbnail} rounded style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover" }} /></div>
              <div className='watch-list-item-description'>
                <p className='watch-list-item-description-title'><AutoTranslate>{item.title}</AutoTranslate></p>
                <span className='watch-list-item-description-summary'><AutoTranslate>{item.summary}</AutoTranslate></span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PlayVideo;